import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Select } from 'src/design/atoms/Select';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { ScimIntegrationDto } from 'src/api/FiretrolApiClient';
import { useApiConnections } from 'src/api/queries/organization/useApiConnections';
import {
  useCreateScimIntegration,
  useDeleteScimIntegration,
  useDisableScimIntegration,
  useUpdateScimIntegration,
} from 'src/api/queries/admin/useScimIntegrationMutations';
import { getErrorMessage } from 'src/utils';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { ModalButton } from 'src/design/molecules/ModalButton';
import { Modal } from 'src/design/molecules/Modal';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { Text } from 'src/design/atoms/Text';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';

const DEFAULT_SCIM_PATH = '/scim/v2';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(255),
  apiConnectionId: Yup.string().required('API connection is required'),
  scimPath: Yup.string()
    .required('SCIM path is required')
    .max(200)
    .matches(/^\//, 'Must start with /'),
  deleteBehavior: Yup.string().required(),
});

interface ScimIntegrationFormProps {
  integration: ScimIntegrationDto | null;
  onClose: () => void;
  onSave: () => void;
}

export const ScimIntegrationForm: React.FC<ScimIntegrationFormProps> = ({
  integration,
  onClose,
  onSave,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const { data: connections = [] } = useApiConnections();
  const createIntegration = useCreateScimIntegration();
  const updateIntegration = useUpdateScimIntegration();
  const disableIntegration = useDisableScimIntegration();
  const deleteIntegration = useDeleteScimIntegration();

  const formik = useFormik({
    initialValues: {
      name: integration?.name ?? '',
      apiConnectionId: integration?.apiConnectionId ?? '',
      scimPath: integration?.scimPath ?? DEFAULT_SCIM_PATH,
      deleteBehavior: integration?.deleteBehavior ?? 'soft_delete',
      isEnabled: integration?.isEnabled ?? true,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        if (integration) {
          await updateIntegration.mutateAsync({
            id: integration.id,
            request: {
              name: values.name,
              apiConnectionId: values.apiConnectionId,
              scimPath: values.scimPath,
              deleteBehavior: values.deleteBehavior,
              isEnabled: values.isEnabled,
            },
          });
        } else {
          await createIntegration.mutateAsync({
            name: values.name,
            apiConnectionId: values.apiConnectionId,
            scimPath: values.scimPath,
            deleteBehavior: values.deleteBehavior,
          });
        }
        onSave();
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      }
    },
  });

  const handleDisable = () => {
    if (!integration) return;
    disableIntegration.mutate(integration.id, { onSuccess: () => onSave() });
  };

  const deleteConfirm = useConfirmableAction(async () => {
    if (!integration) return;
    await deleteIntegration.mutateAsync(integration.id);
    onSave();
  }, true);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  return (
    <>
      <Box>
        <Stack>
          <TextInput
            label="Name"
            placeholder="Integration name..."
            required
            value={formik.values.name}
            onChange={(e) => formik.setFieldValue('name', e.target.value)}
            onBlur={() => formik.setFieldTouched('name')}
            error={formik.touched.name && formik.errors.name}
            onKeyDown={handleKeyDown}
          />

          <Select
            label="API Connection"
            description="The host and API key come from the connection."
            placeholder="Select a connection..."
            required
            data={connections.map((c) => ({
              value: c.id,
              label: c.isEnabled ? c.name : `${c.name} (disabled)`,
            }))}
            value={formik.values.apiConnectionId || null}
            onChange={(val) =>
              formik.setFieldValue('apiConnectionId', val ?? '')
            }
            onBlur={() => formik.setFieldTouched('apiConnectionId')}
            error={
              formik.touched.apiConnectionId && formik.errors.apiConnectionId
            }
          />

          <TextInput
            label="SCIM Path"
            description="Appended to the connection host."
            placeholder={DEFAULT_SCIM_PATH}
            required
            value={formik.values.scimPath}
            onChange={(e) => formik.setFieldValue('scimPath', e.target.value)}
            onBlur={() => formik.setFieldTouched('scimPath')}
            error={formik.touched.scimPath && formik.errors.scimPath}
            onKeyDown={handleKeyDown}
          />

          <Select
            label="Delete Behavior"
            data={[
              { value: 'soft_delete', label: 'Soft Delete (deactivate)' },
              { value: 'hard_delete', label: 'Hard Delete' },
            ]}
            value={formik.values.deleteBehavior}
            onChange={(val) =>
              formik.setFieldValue('deleteBehavior', val ?? 'soft_delete')
            }
          />

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}
        </Stack>
      </Box>

      <Group justify="space-between" mt="md">
        {integration && (
          <Group gap="xs">
            <InlineConfirmButton
              color="red"
              confirmButtonProps={{ children: 'Confirm Disable' }}
              onConfirmClick={handleDisable}
            >
              Disable Integration
            </InlineConfirmButton>
            <DeleteButton
              color="red.9"
              onClick={() => deleteConfirm.requestOrRun()}
            >
              Delete Integration
            </DeleteButton>
            <Modal
              title="Delete Integration"
              opened={deleteConfirm.opened}
              onClose={deleteConfirm.close}
              actions={[
                <DeleteButton
                  key="delete"
                  loading={deleteConfirm.loading}
                  onClick={() => deleteConfirm.confirm()}
                />,
              ]}
            >
              <Text>
                {`Are you sure you want to delete "${integration.name}"? Its user mappings and sync logs are removed with it.`}
              </Text>
            </Modal>
          </Group>
        )}
        <Group>
          <ModalButton onClick={onClose} color="gray" variant="light">
            Cancel
          </ModalButton>
          <ModalButton
            onClick={() => formik.handleSubmit()}
            loading={formik.isSubmitting}
            disabled={!formik.isValid || (!!integration && !formik.dirty)}
          >
            {integration ? 'Save Changes' : 'Create Integration'}
          </ModalButton>
        </Group>
      </Group>
    </>
  );
};
